'use client';

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'motion/react';
import { Drawer as VaulDrawer } from 'vaul';
import { cn } from '@/lib/utils';

interface DrawerContextProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

const DrawerContext = createContext<DrawerContextProps | undefined>(undefined);

export const useResponsiveModal = () => {
  const context = useContext(DrawerContext);
  if (!context) {
    throw new Error(
      'useResponsiveModal must be used within a ResponsiveModalProvider'
    );
  }
  return context;
};

interface ResponsiveModalProps {
  children: ReactNode;
  /** Optional non-scrollable header area rendered below the grabber and above the scrollable content */
  header?: ReactNode;
  open?: boolean;
  setOpen?: (open: boolean) => void;
  classname?: string;
  clsBtnClassname?: string;
  direction?: 'top' | 'bottom';
  drawerClassName?: string;
  /** Force center popup style even on mobile (for auth drawers) */
  alwaysCenter?: boolean;
}

export function ResponsiveModal({
  children,
  header,
  open: controlledOpen,
  setOpen: controlledSetOpen,
  classname,
  clsBtnClassname,
  direction = 'bottom',
  drawerClassName,
  alwaysCenter = false,
}: ResponsiveModalProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [visualViewportHeight, setVisualViewportHeight] = useState<number | null>(null);
  const drawerContentRef = useRef<HTMLDivElement>(null);
  const desktopModalRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = controlledSetOpen || setInternalOpen;

  useEffect(() => {
    // Set isDesktop immediately to prevent flicker
    const mediaQuery = window.matchMedia('(min-width: 768px)');
    setIsDesktop(mediaQuery.matches);
    setMounted(true);
    
    const handleMediaChange = (event: MediaQueryListEvent) => {
      setIsDesktop(event.matches);
    };

    mediaQuery.addEventListener('change', handleMediaChange);
    return () => mediaQuery.removeEventListener('change', handleMediaChange);
  }, []);

  // Move focus to modal/drawer when opening
  useEffect(() => {
    if (open && mounted) {
      const timer = setTimeout(() => {
        if (isDesktop && desktopModalRef.current) {
          desktopModalRef.current.focus();
        } else if (!isDesktop && drawerContentRef.current) {
          drawerContentRef.current.focus();
        }
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [open, mounted, isDesktop]);

  // Handle Visual Viewport API for mobile keyboard detection
  useEffect(() => {
    if (!mounted || isDesktop) return;

    const updateViewportHeight = () => {
      if (window.visualViewport) {
        setVisualViewportHeight(window.visualViewport.height);
      } else {
        setVisualViewportHeight(window.innerHeight);
      }
    };

    // Set initial height
    updateViewportHeight();

    // Listen to visual viewport changes (keyboard open/close)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', updateViewportHeight);
      window.visualViewport.addEventListener('scroll', updateViewportHeight);
    } else {
      // Fallback for browsers without Visual Viewport API
      window.addEventListener('resize', updateViewportHeight);
    }

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', updateViewportHeight);
        window.visualViewport.removeEventListener('scroll', updateViewportHeight);
      } else {
        window.removeEventListener('resize', updateViewportHeight);
      }
    };
  }, [mounted, isDesktop]);

  // Handle input focus to scroll into view within the scroll container
  useEffect(() => {
    if (!mounted || isDesktop || !open || !scrollContainerRef.current) return;

    const handleInputFocus = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.getAttribute('role') === 'textbox' ||
        target.contentEditable === 'true'
      ) {
        const scrollContainer = scrollContainerRef.current;
        if (!scrollContainer) return;

        // Small delay to ensure keyboard animation starts and viewport updates
        setTimeout(() => {
          const containerRect = scrollContainer.getBoundingClientRect();
          const targetRect = target.getBoundingClientRect();
          
          // Calculate if input is visible within the scroll container
          const isVisible = 
            targetRect.top >= containerRect.top &&
            targetRect.bottom <= containerRect.bottom;
          
          if (!isVisible) {
            // Scroll the container to show the input
            const scrollTop = scrollContainer.scrollTop;
            const targetOffsetTop = target.offsetTop;
            const containerHeight = scrollContainer.clientHeight;
            const targetHeight = target.offsetHeight;
            
            // Center the input in the visible area, accounting for some padding
            const desiredScrollTop = targetOffsetTop - (containerHeight / 2) + (targetHeight / 2);
            
            scrollContainer.scrollTo({
              top: Math.max(0, desiredScrollTop),
              behavior: 'smooth',
            });
          }
        }, 150);
      }
    };

    const scrollContainer = scrollContainerRef.current;
    scrollContainer.addEventListener('focusin', handleInputFocus, true);

    return () => {
      scrollContainer.removeEventListener('focusin', handleInputFocus, true);
    };
  }, [mounted, isDesktop, open]);

  const allChildren = React.Children.toArray(children);
  const trigger = allChildren.find(
    (child: any) => child?.type === ResponsiveModalTrigger
  );
  const content = allChildren.filter(
    (child: any) => child?.type !== ResponsiveModalTrigger
  );
  
  // If no trigger found and we have controlled open/setOpen, use all children as content
  const finalContent = trigger ? content : allChildren;

  // Don't render drawer on desktop at all, and don't render anything until mounted
  if (!mounted) {
    return (
      <DrawerContext.Provider value={{ open, setOpen }}>
        {trigger && trigger}
      </DrawerContext.Provider>
    );
  }

  const shouldUseCenterModal = isDesktop || alwaysCenter;
  
  const desktopModal =
    shouldUseCenterModal && open
      ? createPortal(
          <AnimatePresence>
            {open && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className='fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-xs cursor-zoom-out'
                onClick={() => setOpen(false)}
              >
                <motion.div
                  ref={desktopModalRef}
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.9, opacity: 0 }}
                  transition={{ type: 'spring', duration: 0.5 }}
                  onClick={(e) => e.stopPropagation()}
                  className={cn(
                    'relative w-[90%] sm:w-[90%] md:w-full max-w-md mx-auto border border-border bg-card rounded-2xl overflow-hidden cursor-default focus:outline-none focus-visible:outline-none',
                    classname
                  )}
                  tabIndex={-1}
                  role="dialog"
                  aria-modal="true"
                  aria-label="Profile dialog"
                >
                  {/* Optional non-scrollable header for desktop */}
                  {header && (
                    <div className="bg-card">
                      {header}
                    </div>
                  )}
                  <div className="max-h-[90vh] overflow-y-auto">
                    {finalContent}
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>,
          document.body
        )
      : null;

  return (
    <DrawerContext.Provider value={{ open, setOpen }}>
      {trigger && trigger}

      {desktopModal}

      {!shouldUseCenterModal && mounted && (
        <VaulDrawer.Root
          direction={direction}
          shouldScaleBackground={false}
          open={open}
          onOpenChange={setOpen}
          modal={true}
        >
          <VaulDrawer.Portal>
            <VaulDrawer.Overlay 
              className='fixed inset-0 z-50 bg-black/50 backdrop-blur-xs' 
              onClick={(e) => {
                const target = e.target as HTMLElement;
                
                // Check if click is directly on Select portal content
                let currentElement: HTMLElement | null = target;
                while (currentElement && currentElement !== document.body) {
                  if (
                    currentElement.getAttribute('role') === 'listbox' ||
                    currentElement.hasAttribute('data-radix-select-content') ||
                    currentElement.hasAttribute('data-radix-select-viewport') ||
                    currentElement.hasAttribute('data-radix-select-item') ||
                    currentElement.hasAttribute('data-radix-select-scroll-up-button') ||
                    currentElement.hasAttribute('data-radix-select-scroll-down-button') ||
                    currentElement.closest('[role="listbox"]') ||
                    currentElement.closest('[data-radix-popper-content-wrapper]') ||
                    currentElement.closest('[data-radix-select-content]')
                  ) {
                    // Click is on Select - let Select handle it, don't close drawer
                    return;
                  }
                  currentElement = currentElement.parentElement;
                }
                
                // Check if any Select dropdown is currently open
                // If Select is open, clicking outside will close Select (handled by Radix)
                // We should NOT close the drawer in this case
                const openSelects = document.querySelectorAll('[role="listbox"]');
                if (openSelects.length > 0) {
                  // Select is open - let Radix handle closing it, don't close drawer
                  return;
                }
                
                // No Select is open - safe to close drawer
                setOpen(false);
              }}
            />
            <VaulDrawer.Content 
              ref={drawerContentRef}
              className={cn(
                'fixed left-0 right-0 z-50 w-full bg-card focus:outline-none focus-visible:outline-none overflow-hidden',
                direction === 'top' 
                  ? 'top-0 rounded-b-2xl' 
                  : 'bottom-0 rounded-t-2xl',
                drawerClassName
              )}
              style={{
                maxHeight: visualViewportHeight 
                  ? `${Math.min(visualViewportHeight * 0.96, window.innerHeight * 0.96)}px`
                  : '96vh'
              }}
              onOpenAutoFocus={(e) => {
                e.preventDefault();
                drawerContentRef.current?.focus();
              }}
              onInteractOutside={(e) => {
                // Prevent closing drawer when clicking on Select dropdown portals
                const target = e.target as HTMLElement;
                
                // Check if click is on any Radix UI Select portal element
                let currentElement: HTMLElement | null = target;
                while (currentElement && currentElement !== document.body) {
                  if (
                    currentElement.getAttribute('role') === 'listbox' ||
                    currentElement.hasAttribute('data-radix-select-content') ||
                    currentElement.hasAttribute('data-radix-select-viewport') ||
                    currentElement.hasAttribute('data-radix-select-item') ||
                    currentElement.hasAttribute('data-radix-select-scroll-up-button') ||
                    currentElement.hasAttribute('data-radix-select-scroll-down-button') ||
                    currentElement.closest('[role="listbox"]') ||
                    currentElement.closest('[data-radix-popper-content-wrapper]') ||
                    currentElement.closest('[data-radix-select-content]')
                  ) {
                    e.preventDefault();
                    return;
                  }
                  currentElement = currentElement.parentElement;
                }
                
                // Check if any Select dropdown is currently open
                // This prevents drawer from closing when Select is handling its own close
                const openSelects = document.querySelectorAll('[role="listbox"]');
                if (openSelects.length > 0) {
                  e.preventDefault();
                  return;
                }
              }}
              tabIndex={-1}
            >
              {/* Visually hidden elements for accessibility - prevents Dialog warnings */}
              <VaulDrawer.Title className="sr-only">Profile Information</VaulDrawer.Title>
              <VaulDrawer.Description className="sr-only">
                View profile details and contact options
              </VaulDrawer.Description>
                <div className={cn('bg-card min-h-full overflow-hidden', direction === 'top' ? 'rounded-b-2xl' : 'rounded-t-2xl')}>
                <div className={cn('mx-auto w-16 h-[0.30rem] shrink-0 rounded-full bg-muted', direction === 'top' ? 'mt-2 mb-2' : 'my-2')} />
                {/* Optional non-scrollable header area directly below grabber */}
                {header && (
                  <div className="bg-card px-2 pb-2">
                    {header}
                  </div>
                )}
                <div 
                  ref={scrollContainerRef}
                  className={cn('w-full overflow-auto px-2 focus:outline-none bg-card', direction === 'top' ? 'rounded-b-2xl pb-2' : 'rounded-t-2xl pb-safe')}
                  style={{
                    maxHeight: visualViewportHeight 
                      ? `${Math.min(visualViewportHeight * 0.96, window.innerHeight * 0.96)}px`
                      : '96vh',
                    paddingBottom: visualViewportHeight && visualViewportHeight < window.innerHeight * 0.8
                      ? '8rem' // Extra padding when keyboard is open
                      : '0.5rem',
                    borderTopLeftRadius: direction === 'bottom' ? '1rem' : '0',
                    borderTopRightRadius: direction === 'bottom' ? '1rem' : '0',
                    borderBottomLeftRadius: direction === 'top' ? '1rem' : '0',
                    borderBottomRightRadius: direction === 'top' ? '1rem' : '0'
                  }}
                >
                  {finalContent}
                </div>
              </div>
            </VaulDrawer.Content>
          </VaulDrawer.Portal>
        </VaulDrawer.Root>
      )}
    </DrawerContext.Provider>
  );
}

export function ResponsiveModalContent({
  children,
  className,
  titleId,
}: {
  children: ReactNode;
  className?: string;
  titleId?: string;
}) {
  return (
    <div 
      className={cn('', className)}
      aria-labelledby={titleId}
      tabIndex={-1}
    >
      {children}
    </div>
  );
}

interface ResponsiveModalTriggerProps {
  children: ReactNode;
  asChild?: boolean;
}

export function ResponsiveModalTrigger({
  children,
  asChild = false,
}: ResponsiveModalTriggerProps) {
  const { setOpen } = useResponsiveModal();

  if (asChild) {
    // Clone the child element and add onClick handler
    const child = React.Children.only(children) as React.ReactElement<any>;
    return React.cloneElement(child, {
      ...child.props,
      onClick: (e: React.MouseEvent) => {
        setOpen(true);
        // Call original onClick if it exists
        child.props.onClick?.(e);
      },
    } as any);
  }

  return <div onClick={() => setOpen(true)}>{children}</div>;
}
